import { TmBaseComponent } from '@treasury/presentation';
import { attachIcon } from '@treasury/presentation/assets/icons';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

type FileNameAndSizeList = string;
type FileExtensions = string;
export type InvalidFileSizeEvent = CustomEvent<FileNameAndSizeList>;
export type InvalidFileExtensionEvent = CustomEvent<FileExtensions>;
export type FileUploadEvent = CustomEvent<{ files: FileList }>;

export const tagName = 'file-uploader';
@customElement(tagName)
export class FileUploader extends TmBaseComponent {
    @property()
    public acceptedFileExtensions: string[] = [];

    @property()
    public fileName?: string;

    @property()
    public maxSizeMb = 4;

    @property()
    public disabled = false;

    @property()
    public label = `file-upload-${this.maxSizeMb}MB`;

    @property({ type: Boolean, reflect: true })
    public showPreview = false;

    @state()
    private files: File[] = [];

    @state()
    private filePreviews: string[] = [];

    @state()
    private _id = '';

    public get id() {
        return (
            this._id ||
            this.label.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase())
        );
    }

    public set id(value) {
        this._id = value;
    }

    public extensionIsInvalid(file: File) {
        return this.acceptedFileExtensions.every(
            (ext: string) => !ext.includes(file.name.split('.').pop() as string)
        );
    }

    public fileSizeIsInvalid(file: File) {
        return file.size >= this.maxSizeMb * 1000000;
    }

    public get fileNames() {
        return this.files?.map(file => file.name);
    }

    public get preview() {
        return this.shadowRoot?.querySelector('#file-upload-preview');
    }

    public render() {
        const accept =
            Array.isArray(this.acceptedFileExtensions) && this.acceptedFileExtensions.length > 0
                ? this.acceptedFileExtensions.join(', ')
                : '*';
        const fileName = this.files[0]?.name ?? this.fileName ?? 'Attachment...';
        return html`
            <input
                .accept=${accept}
                type="file"
                id=${this.id}
                ?disabled=${this.disabled}
                ?multiple=${false}
                @change=${this.fileUpload}
            />
            <label for=${this.id}>
                <div class="flex items-center w-full h-full px-4">
                    <tm-icon .icon=${attachIcon} class="my-1 mr-3"></tm-icon>
                    <div>${fileName}</div>
                </div>
            </label>
            <div id="file-upload-preview"></div>
        `;
    }

    public fileUpload(e: Event) {
        const target = <HTMLInputElement>e.target;
        if (target && target.files?.length) {
            this.files = [...target.files];
            target.value = '';
            if (this._hasInvalidFileSizes()) {
                this.dispatchEvent(
                    new CustomEvent('invalidFileSize', {
                        bubbles: true,
                        composed: true,
                        detail: this.files
                            .map(file => `${file.name} - ${Math.round(file.size / 100000) / 10}MB`)
                            .join(', '),
                    }) as InvalidFileSizeEvent
                );
                this.files = [];
                return;
            }
            if (this._hasInvalidFileExtension()) {
                this.dispatchEvent(
                    new CustomEvent('invalidFileExtension', {
                        bubbles: true,
                        composed: true,
                        detail: this.files
                            .filter(file => this.extensionIsInvalid(file))
                            .map(file => `.${file.name.split('.').pop()}`)
                            .join(', '),
                    })
                );
                this.files = [];
                return;
            }
            if (this.preview) {
                this.preview.textContent = '';
            }
            this.filePreviews = this.files.map(file => {
                if (file.type.includes('image')) {
                    this.addImagePreview(file);
                }
                return '';
            });
        } else {
            this.files = [];
        }

        this.dispatchEvent(
            new CustomEvent('filesUploaded', {
                bubbles: true,
                composed: true,
                detail: { files: this.files },
            })
        );
    }

    private _hasInvalidFileSizes() {
        return this.files.some(file => this.fileSizeIsInvalid(file));
    }

    /**
     * For mobile web, some devices ignore the accepted file extension on the input tag.
     * https://caniuse.com/input-file-accept
     */
    private _hasInvalidFileExtension() {
        return !!this.files.find(file => this.extensionIsInvalid(file));
    }

    // Escape HTML entities in a string for safe DOM insertion
    private escapeHtml(text: string): string {
        const map: { [key: string]: string } = {
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#039;',
        };
        return text.replace(/[&<>"']/g, function (m) {
            return map[m];
        });
    }

    private addImagePreview(file: File) {
        // Define allowed image types and extensions (SVG explicitly blocked)
        const allowedTypes = ['image/png', 'image/jpeg', 'image/gif'];
        const allowedExts = ['png', 'jpg', 'jpeg', 'gif'];
        // Get file type and extension in lowercase
        const type = (file.type || '').toLowerCase();
        const fileExt = file.name.split('.').pop()?.toLowerCase() || '';
        // Block SVG files explicitly by type and extension
        if (type === 'image/svg+xml' || fileExt === 'svg') {
            // Optionally dispatch event or inform user of invalid preview type
            return;
        }
        // Only allow files where both type and extension are allowed
        const isAllowedType = allowedTypes.includes(type);
        const isAllowedExt = allowedExts.includes(fileExt);
        if (!isAllowedType || !isAllowedExt) {
            return;
        }
        const previewImg = document.createElement('img');
        previewImg.src = URL.createObjectURL(file);
        // Escape file.name to prevent DOM injection
        previewImg.alt = this.escapeHtml(file.name);
        if (this.preview) {
            const safePreviewImg = previewImg.cloneNode(true) as HTMLImageElement;
            this.preview.appendChild(safePreviewImg);
        }
    }

    public static get styles() {
        return [
            css`
                :host {
                    display: block;
                    cursor: pointer;
                    height: 52px;
                    width: 100%;
                }
                .label-content {
                    display: flex;
                    align-items: center;
                    width: 100%;
                    height: 100%;
                    padding: 0 16px;
                }
                input {
                    display: none;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: FileUploader;
    }
}
